import { useEffect, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import {
  CloseCircleOutline,
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  SearchNormalOutline,
  TickCircleOutline,
  Transaction,
  TransactionCategory,
  TransactionDirection,
  TransactionStatus,
} from '@aslanpay/portal-common-lib';

import AddDeductionAdjustmentButton from 'app/components/DeductionAdjustments/AddDeductionAdjustmentButton';
import CurrencyAmount from 'app/components/UI/CurrencyAmount/CurrencyAmount';
import { Table } from 'app/components/UI/Table/Table';
import { TransactionActions } from 'app/components/UI/Table/Table.types';

import {
  TransactionCategoryLabels,
  TransactionDirectionLabels,
  TransactionStatusLabels,
} from 'app/constants/transactions';

import useDebounce from 'app/hooks/useDebounce';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useFetchTransactionsPage from 'app/hooks/useFetchTransactionsPage';

import getTransactionColumns from 'app/utils/table/transactions';

import { formatCurrency, getPayCycleName } from 'app/utils/strings';

import { useSearchParams } from 'react-router-dom';

import AdjustBalanceModal from './AdjustBalanceModal/AdjustBalanceModal';
import { SalaryCutoffHoldBanner, SalaryCutoffHoldNote } from './SalaryCutoffHold';
import TransactionDetails from './TransactionDetails';
import { IndividualWalletTabProps } from './types';

import type { Filter } from '@aslanpay/portal-common-lib';

const transactionDirectionOptions: DropdownOption[] = Object.entries(
  TransactionDirectionLabels
).map(([value, text]) => ({
  value,
  text,
}));

const transactionCategoryOptions: DropdownOption[] = Object.entries(TransactionCategoryLabels).map(
  ([value, text]) => ({
    value,
    text,
  })
);

const transactionStatusOptions: DropdownOption[] = Object.entries(TransactionStatusLabels).map(
  ([value, text]) => ({
    value,
    text,
  })
);

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
  { value: 100, text: '100 rows / page' },
];

function IndividualWalletTab({ individual, wallet }: IndividualWalletTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    isOpen: isAdjustBalanceOpen,
    onOpen: onOpenAdjustBalance,
    onClose: onCloseAdjustBalance,
  } = useDisclosure();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
  const [directionFilter, setDirectionFilter] = useState<TransactionDirection[]>(
    (searchParams.get('directions')?.split(',') as TransactionDirection[]) || []
  );
  const [categoriesFilter, setCategoriesFilter] = useState<TransactionCategory[]>(
    (searchParams.get('categories')?.split(',') as TransactionCategory[]) || []
  );
  const [statusFilter, setStatusFilter] = useState<TransactionStatus[]>(
    (searchParams.get('status')?.split(',') as TransactionStatus[]) || []
  );
  const [dateFilter, setDateFilter] = useState<{ startDate: string; endDate: string } | undefined>(
    searchParams.get('startDate') && searchParams.get('endDate')
      ? {
          startDate: searchParams.get('startDate') as string,
          endDate: searchParams.get('endDate') as string,
        }
      : undefined
  );
  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: 100,
  });
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  // should check the company but not available here - using individual for now
  const isIndividualGiftingOnly = !individual?.payCalendarId;

  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    individual?.payCalendarId
  );

  const { data: transactions, isLoading: isLoadingTransactions } = useFetchTransactionsPage({
    individualIds: [individual?.id || ''],
    companyIds: [individual?.companyId || ''],
    directions: directionFilter,
    categories: categoriesFilter,
    status: statusFilter,
    startDate: dateFilter?.startDate || '',
    endDate: dateFilter?.endDate || '',
    query: debouncedSearchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const payCyclesOptions: DropdownOption[] = [
    { value: 'all', text: 'All pay cycles' },
    ...(payCalendar?.payCycles.map((payCycle) => {
      const text = getPayCycleName(payCycle, payCalendar.payFrequency);
      return {
        value: payCycle.id,
        text,
      };
    }) || []),
  ];

  const filters: Filter[] = [
    {
      name: 'Transaction direction',
      type: 'category',
      data: transactionDirectionOptions,
      selectedValues: directionFilter,
    },
    {
      name: 'Transaction category',
      type: 'category',
      data: transactionCategoryOptions,
      selectedValues: categoriesFilter,
    },
    {
      name: 'Transaction status',
      type: 'category',
      data: transactionStatusOptions,
      selectedValues: statusFilter,
    },
    {
      name: 'Pay cycles',
      type: 'category',
      data: payCyclesOptions,
      selectedValues: (() => {
        if (!dateFilter) return [];

        const payCycleId = payCalendar?.payCycles.find(
          (pc) => pc.startDate === dateFilter.startDate && pc.endDate === dateFilter.endDate
        )?.id;

        return payCycleId ? [payCycleId] : [];
      })(),

      singleSelection: true,
    },
  ];

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  const resetPagination = () => {
    setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const handleDirectionChange = (values: (string | number)[]) => {
    setDirectionFilter(values as TransactionDirection[]);
    resetPagination();
  };

  const handleCategoryChange = (values: (string | number)[]) => {
    setCategoriesFilter(values as TransactionCategory[]);
    resetPagination();
  };

  const handleStatusChange = (values: (string | number)[]) => {
    setStatusFilter(values as TransactionStatus[]);
    resetPagination();
  };

  const handlePayCycleChange = (value: string | number | undefined) => {
    if (!value || value === 'all') {
      setDateFilter(undefined);
      resetPagination();
      return;
    }

    const selectedCycle = payCalendar?.payCycles.find((pc) => pc.id === value);

    if (selectedCycle) {
      setDateFilter({
        startDate: selectedCycle.startDate,
        endDate: selectedCycle.endDate,
      });
      resetPagination();
    }
  };

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    switch (filterName) {
      case 'Search': {
        const value = newValues[0] || '';

        setSearchQuery(value);
        setDebouncedSearchQuery(value);
        resetPagination();
        break;
      }
      case 'Transaction direction':
        handleDirectionChange(newValues);
        break;
      case 'Transaction category':
        handleCategoryChange(newValues);
        break;
      case 'Transaction status':
        handleStatusChange(newValues);
        break;
      case 'Pay cycles':
        if (newValues.length === 0) {
          handlePayCycleChange('all');
        } else {
          const selectedCycleId = newValues[newValues.length - 1];
          handlePayCycleChange(selectedCycleId);
        }
        break;
      default:
        break;
    }
  };

  const debouncedTyping = useDebounce((value: string) => {
    setDebouncedSearchQuery(value);
    resetPagination();
  }, 500);

  const onSearchInput = (value: string) => {
    setSearchQuery(value);
    debouncedTyping(value);
  };

  const actions: TransactionActions = {
    select: (transaction: Transaction) => setSelectedTransaction(transaction),
    selectedTransaction,
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    params.set('page', (pageIndex + 1).toString());

    if (debouncedSearchQuery) {
      params.set('query', debouncedSearchQuery);
    } else {
      params.delete('query');
    }

    if (directionFilter.length > 0) {
      params.set('directions', directionFilter.join(','));
    } else {
      params.delete('directions');
    }

    if (categoriesFilter.length > 0) {
      params.set('categories', categoriesFilter.join(','));
    } else {
      params.delete('categories');
    }

    if (statusFilter.length > 0) {
      params.set('status', statusFilter.join(','));
    } else {
      params.delete('status');
    }

    if (dateFilter?.startDate && dateFilter?.endDate) {
      params.set('startDate', dateFilter.startDate);
      params.set('endDate', dateFilter.endDate);
    } else {
      params.delete('startDate');
      params.delete('endDate');
    }

    if (sorting[0]) {
      params.set('sortBy', sorting[0].id);
      params.set('sort', sorting[0].desc ? 'DESC' : 'ASC');
    } else {
      params.delete('sortBy');
      params.delete('sort');
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    pageIndex,
    debouncedSearchQuery,
    directionFilter,
    categoriesFilter,
    statusFilter,
    dateFilter,
    sorting,
    setSearchParams,
    searchParams,
  ]);

  const individualName = individual
    ? `${individual.firstName} ${individual.lastName}`.trim() || individual.email
    : undefined;

  return (
    <Flex flexDir="column" w="full" gap="20px">
      <AdjustBalanceModal
        isOpen={isAdjustBalanceOpen}
        onClose={onCloseAdjustBalance}
        wallet={wallet}
        individualName={individualName}
      />
      <Flex
        p="16px"
        gap="24px"
        flexDir="row"
        w="full"
        bgColor="brand.lightGray2"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
      >
        {/* Overall Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Balance</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.balance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingCredit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
        </Flex>

        {/* Refund Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Refund</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.refundBalance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingRefundDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingRefundCredit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
        </Flex>

        {/* Chargeback Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Chargeback</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.chargebackBalance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingChargebackDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
        </Flex>

        {/* Cashback Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Cashback</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.cashbackBalance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingCashbackDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
        </Flex>

        {/* Reserve Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Set & Spend</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.reserveBalance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingReserveDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingReserveCredit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
        </Flex>

        {/* Salary Balance */}
        <Flex flexDir="column" w="full" gap="8px">
          <Text textStyle="smallBold">Salary</Text>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular">Balance</Text>
            <CurrencyAmount
              amount={wallet?.salaryBalance || 0}
              currency={wallet?.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending debit
            </Text>
            <CurrencyAmount
              amount={wallet?.pendingSalaryDebit || 0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Pending credit
            </Text>
            <CurrencyAmount
              amount={0}
              currency={wallet?.currency}
              textStyle="extraSmallRegular"
              color="brand.darkGray"
            />
          </Flex>
          <SalaryCutoffHoldNote amount={wallet?.pendingSalaryCutoff} currency={wallet?.currency} />
        </Flex>
      </Flex>

      <Flex justify="space-between" align="flex-end">
        {/* Spend Cap */}
        <Flex
          p="16px"
          gap="12px"
          flexDir="row"
          alignItems="center"
          alignSelf="flex-start"
          bgColor="brand.lightGray2"
          borderRadius="10px"
          border="1px solid"
          borderColor="brand.lightGray1"
          minW="220px"
        >
          <Flex flexDir="column" gap="4px">
            <Flex align="center" gap="6px">
              {wallet?.userSpendCap != null ? (
                <TickCircleOutline boxSize="16px" color="system.success" />
              ) : (
                <CloseCircleOutline boxSize="16px" color="system.disabled" />
              )}
              <Text textStyle="smallBold">Spend cap</Text>
            </Flex>
            <Flex justify="space-between" gap="32px">
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                User spend cap
              </Text>
              {wallet?.userSpendCap != null ? (
                <CurrencyAmount
                  amount={wallet.userSpendCap}
                  currency={wallet.currency}
                  textStyle="extraSmallMedium"
                />
              ) : (
                <Text textStyle="extraSmallMedium" color="brand.darkGray">
                  Not set
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex gap="8px">
          <AddDeductionAdjustmentButton individual={individual} />
          <Button
            size="sm"
            colorScheme="purple"
            data-testid="adjust-balance-button"
            isDisabled={!wallet?.id}
            onClick={onOpenAdjustBalance}
          >
            Adjust balance
          </Button>
        </Flex>
      </Flex>

      <Flex>
        {!isIndividualGiftingOnly &&
          (isLoadingPayCalendar ? (
            <Center width="full" height="full" flexGrow={1}>
              <Spinner color="brand.purple" size="xl" mb="50px" />
            </Center>
          ) : (
            <Filters
              activeFilter={activeFilter}
              displayFormatter={formatFilterValue}
              filters={filters}
              onFilterClose={() => setActiveFilter(undefined)}
              onFilterChange={handleFilterChange}
              onFilterClick={handleFilterClick}
            />
          ))}
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !transactions?.list.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
          marginLeft="auto"
        >
          <Input
            isDisabled={!searchQuery.length && !transactions?.list.length}
            onChange={(event) => onSearchInput(event.target.value)}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>
      <Flex w="full" gap="10px" align="center">
        <Text textStyle="smallMedium">{transactions?.total} total transactions</Text>
        <Text textStyle="smallMedium">
          <strong>Sum amount: </strong>
          {formatCurrency(
            transactions?.list
              .map((transaction) => transaction.amount)
              .reduce((a, b) => a + b, 0) || 0,
            wallet?.currency
          )}
        </Text>
        <Text textStyle="smallMedium">
          <strong>Sum fx fees: </strong>
          {formatCurrency(
            transactions?.list.map((transaction) => transaction.fxFee).reduce((a, b) => a + b, 0) ||
              0,
            wallet?.currency
          )}
        </Text>
        <Text textStyle="smallMedium">
          <strong>Sum total amount: </strong>
          {formatCurrency(
            transactions?.list
              .map((transaction) => transaction.totalAmount)
              .reduce((a, b) => a + b, 0) || 0,
            wallet?.currency
          )}
        </Text>
        <Text
          p="8px"
          borderRadius="10px"
          alignSelf="flex-start"
          bgColor="alert.error"
          color="system.error"
          textStyle="extraSmallBold"
        >
          These totals only apply to the current page.
        </Text>
      </Flex>
      <SalaryCutoffHoldBanner amount={wallet?.pendingSalaryCutoff} currency={wallet?.currency} />
      <Flex flexDir="row" w="full" gap="10px">
        <Grid templateColumns="repeat(4, 1fr)" gap={6}>
          <GridItem colSpan={3}>
            {isLoadingTransactions ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getTransactionColumns(actions)}
                data={transactions?.list || []}
                total={transactions?.total || 0}
                pageNumber={pageIndex}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
                getRowId={(transaction) => transaction.id}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
              />
            )}
          </GridItem>
          <GridItem colSpan={1}>
            <Flex
              flexDir="column"
              gap="10px"
              height="calc(100vh - 200px)"
              position="sticky"
              top="100px"
            >
              <Text textStyle="smallMedium">Transaction details</Text>
              <Flex flexDir="column" overflowY="auto" flex="1">
                <TransactionDetails transaction={selectedTransaction} />
              </Flex>
            </Flex>
          </GridItem>
        </Grid>
      </Flex>
    </Flex>
  );
}

export default IndividualWalletTab;
